import { useState, useEffect, useRef } from 'react'
import { format, isBefore, startOfDay } from 'date-fns'
import type { WeekDay, WeeklySchedule, TimeSlot, ArtistAvailability } from '@/types/booking'
import { DEFAULT_WEEKLY_SCHEDULE } from '@/types/booking'
import { WEEKDAYS, hasTimeSlotOverlap, MAX_TIME_SLOTS, formatTimeRange } from '@/lib/time-utils'
import { AvailabilityDayRow } from './AvailabilityDayRow'
import { Button } from '@/components/ui/button'
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react'
import { MonthlyAvailability } from './MonthlyAvailability'
import { toast } from 'sonner'

interface WeeklyAvailabilityProps {
  availability?: ArtistAvailability | null
  onSave: (schedule: WeeklySchedule) => Promise<void>
  onSaveSpecificDates: (dates: Date[], timeSlots: TimeSlot[]) => Promise<void>
}

export function WeeklyAvailability({
  availability,
  onSave,
  onSaveSpecificDates
}: WeeklyAvailabilityProps) {
  const [schedule, setSchedule] = useState<WeeklySchedule>(DEFAULT_WEEKLY_SCHEDULE)
  const [isSpecificDatesOpen, setIsSpecificDatesOpen] = useState(false)
  const [showOverrides, setShowOverrides] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (availability?.weekSchedule) {
      setSchedule(availability.weekSchedule)
    }
  }, [availability])

  useEffect(() => {
    // Scroll the container to position Monday nicely on load
    if (containerRef.current) {
      setTimeout(() => {
        containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }
  }, [])

  const saveSchedule = async (newSchedule: WeeklySchedule) => {
    try {
      await onSave(newSchedule)
      toast.success('Schedule updated')
    } catch (error) {
      console.error('Failed to update schedule:', error)
      toast.error('Failed to update schedule')
    }
  }

  const handleToggleDay = (day: WeekDay) => {
    const newSchedule = {
      ...schedule,
      [day]: {
        isEnabled: true,
        timeSlots: schedule[day].isEnabled && schedule[day].timeSlots.length > 0 
          ? [] 
          : [{ start: 540, end: 1020 }]
      }
    }
    setSchedule(newSchedule)
    void saveSchedule(newSchedule)
  }

  const handleAddTimeSlot = (day: WeekDay) => {
    const currentTimeSlots = schedule[day].timeSlots

    if (currentTimeSlots.length >= MAX_TIME_SLOTS) {
      toast.error(`Maximum ${MAX_TIME_SLOTS} time slots allowed per day`)
      return
    }

    const lastSlot = currentTimeSlots[currentTimeSlots.length - 1]
    const newTimeSlot = lastSlot
      ? { start: lastSlot.end, end: Math.min(lastSlot.end + 480, 1440) }
      : { start: 540, end: 1020 }

    if (hasTimeSlotOverlap(currentTimeSlots, newTimeSlot)) {
      toast.error('Time slots cannot overlap')
      return
    }

    const newSchedule = {
      ...schedule,
      [day]: {
        ...schedule[day],
        timeSlots: [...currentTimeSlots, newTimeSlot]
      }
    }
    setSchedule(newSchedule)
    void saveSchedule(newSchedule)
  }

  const handleRemoveTimeSlot = (day: WeekDay, index: number) => {
    const newSchedule = {
      ...schedule,
      [day]: {
        ...schedule[day],
        timeSlots: schedule[day].timeSlots.filter((_, i) => i !== index)
      }
    }
    setSchedule(newSchedule)
    void saveSchedule(newSchedule)
  }

  const handleCopyDay = (sourceDay: WeekDay, targetDays: WeekDay[]) => {
    const newSchedule = { ...schedule }
    
    targetDays.forEach(targetDay => {
      newSchedule[targetDay] = {
        ...newSchedule[targetDay],
        timeSlots: [...schedule[sourceDay].timeSlots]
      }
    })
    
    setSchedule(newSchedule)
    void saveSchedule(newSchedule)
  }

  const handleUpdateTimeSlot = (day: WeekDay, index: number, timeSlot: TimeSlot) => {
    if (timeSlot.end <= timeSlot.start) {
      toast.error('End time must be after start time')
      return
    }

    const otherTimeSlots = schedule[day].timeSlots.filter((_, i) => i !== index)
    if (hasTimeSlotOverlap(otherTimeSlots, timeSlot)) {
      toast.error('Time slots cannot overlap')
      return
    }

    const newSchedule = {
      ...schedule,
      [day]: {
        ...schedule[day],
        timeSlots: schedule[day].timeSlots.map((slot, i) => 
          i === index ? timeSlot : slot
        )
      }
    }
    setSchedule(newSchedule)
    void saveSchedule(newSchedule)
  }

  // Get future date overrides
  const futureOverrides = availability?.dateOverrides 
    ? Object.entries(availability.dateOverrides)
        .filter(([dateString]) => !isBefore(new Date(dateString), startOfDay(new Date())))
        .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    : []

  return (
    <div ref={containerRef} className="flex flex-col gap-0">
      {/* Weekly Schedule */}
      <div className="bg-card rounded-lg p-4 pt-0 rounded-b-none border-b-0">
        <div className="divide-y divide-border">
          {WEEKDAYS.map(({ id, label }) => (
            <AvailabilityDayRow
              key={id}
              day={id}
              dayLabel={label}
              settings={schedule[id]}
              onToggleDay={handleToggleDay}
              onAddTimeSlot={handleAddTimeSlot}
              onRemoveTimeSlot={handleRemoveTimeSlot}
              onCopyDay={handleCopyDay}
              onUpdateTimeSlot={handleUpdateTimeSlot}
            />
          ))}
        </div>
      </div>

      {/* Specific Date Overrides */}
      <div className="bg-card rounded-lg p-4 rounded-t-none">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold">Date Exceptions</h3>
          <Button 
            size="sm" 
            variant={isSpecificDatesOpen ? "outline" : "default"}
            onClick={() => setIsSpecificDatesOpen(!isSpecificDatesOpen)}
          >
            {isSpecificDatesOpen ? (
              <>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-1" />
                Add
              </>
            )}
          </Button>
        </div>

        {isSpecificDatesOpen ? (
          <MonthlyAvailability
            availability={availability}
            onSave={onSaveSpecificDates}
          />
        ) : (
          <>
            {futureOverrides.length > 0 ? (
              <div className="space-y-2">
                <button
                  onClick={() => setShowOverrides(!showOverrides)}
                  className="w-full flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <span>
                    {futureOverrides.length} exception{futureOverrides.length !== 1 ? 's' : ''} configured
                  </span>
                  {showOverrides ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {showOverrides && (
                  <div className="space-y-2 mt-3">
                    {futureOverrides.map(([dateString, settings]) => (
                      <div 
                        key={dateString} 
                        className="flex items-center justify-between p-3 bg-muted rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-sm">
                            {format(new Date(dateString), 'EEE, MMM d')}
                          </p>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {settings.isEnabled && settings.timeSlots.length > 0 ? (
                              settings.timeSlots.map((slot: TimeSlot, index: number) => (
                                <span key={index}>
                                  {index > 0 && ', '}
                                  {formatTimeRange(slot.start, slot.end)}
                                </span>
                              ))
                            ) : (
                              'Unavailable'
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No date exceptions configured
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
